/**
 * Live preview — the real page, in a device frame.
 *
 * It renders with `renderizarPagina`, the **same** function the public page
 * uses, inside an iframe sized to the chosen device. Reimplementing the blocks
 * in React for the preview would be faster to write and would drift within a
 * week: the operator would approve a layout that production renders
 * differently, which is the one thing a preview exists to prevent.
 *
 * ## Quatro coisas que este arquivo já errou, e por isso estão escritas
 *
 * **`sandbox=""` broke selection.** An empty sandbox gives the iframe a unique
 * opaque origin, so `contentDocument` reads as `null` from the parent and the
 * click listener silently never attaches. Clicking a block did nothing, with no
 * error anywhere. It now carries `allow-same-origin` and *not* `allow-scripts`
 * — which is the combination that matters: the page's own JavaScript still
 * never runs, so no pixel fires and no test lead is posted, but the editor can
 * read the document it just wrote.
 *
 * **Relative image paths resolved against the panel.** Inside `srcDoc` the base
 * URL is the *parent* document's, so `/publico/midia/x` pointed at the panel
 * domain and 404'd — every image blank, alt text showing. A `<base>` tag fixes
 * every relative URL at once, which is better than rewriting them one by one
 * and forgetting the next kind.
 *
 * **The listener attached too early.** With `srcDoc`, the document is replaced
 * on each render; an effect that runs before parsing attaches to the outgoing
 * document. It now hangs off the iframe's own `load`.
 *
 * **A rolagem voltava ao topo a cada tecla.** `srcDoc` recarrega o documento
 * inteiro, e recarregar leva ao topo: quem estava ajustando o texto do rodapé
 * via a prévia pular para o começo da página a cada caractere digitado. A
 * posição é guardada e devolvida — exceto quando a seleção mudou por fora
 * (clique na estrutura), caso em que o certo é rolar até o bloco escolhido.
 */

import { ROTULOS_BLOCO, renderizarPagina, type Bloco, type PaginaLanding } from '@egba-do/shared'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

const URL_API = import.meta.env.VITE_API_URL ?? ''

export type Dispositivo = 'desktop' | 'tablet' | 'celular'

export const LARGURAS: Record<Dispositivo, number> = {
  desktop: 1280,
  tablet: 834,
  celular: 390,
}

function attr(valor: string): string {
  return valor.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;')
}

/** Wraps each rendered section so a click can be traced back to its block. */
function comMarcadores(pagina: PaginaLanding): string {
  const html = renderizarPagina(pagina, { urlApi: URL_API, previa: true })

  // Each block renders exactly one top-level <section> or <footer>; tagging
  // them in order is enough and avoids re-implementing the renderer here.
  const visiveis = pagina.blocos.filter((b) => !b.oculto)
  let i = 0
  const marcado = html.replace(/<(section|footer)(\s|>)/g, (m, tag: string, fim: string) => {
    const b = visiveis[i++]
    if (!b) return m
    const rotulo = ROTULOS_BLOCO[b.tipo]?.nome ?? b.tipo
    return `<${tag} data-bloco="${attr(b.id)}" data-rotulo="${attr(rotulo)}"${fim}`
  })

  const cabeca = `
<base href="${URL_API ? `${URL_API}/` : '/'}">
<style>
  [data-bloco]{position:relative;cursor:pointer}
  [data-bloco]:hover{outline:2px dashed rgba(12,100,176,.55);outline-offset:-2px}
  /* O rótulo aparece no hover e no selecionado: sem ele, saber em que bloco se
     está exige adivinhar pelo conteúdo, e "Texto" e "Prazo" se parecem. */
  [data-bloco]::before{
    content:attr(data-rotulo);position:absolute;top:0;left:0;z-index:9;
    background:#0c64b0;color:#fff;font:600 11px/1 ui-sans-serif,system-ui,sans-serif;
    padding:5px 9px;border-radius:0 0 6px 0;letter-spacing:.04em;text-transform:uppercase;
    opacity:0;transition:opacity .12s;pointer-events:none}
  [data-bloco]:hover::before{opacity:1}
  [data-sel]{outline:3px solid #0c64b0;outline-offset:-3px}
  [data-sel]::before{opacity:1}
</style>`

  return marcado.replace('</head>', `${cabeca}</head>`)
}

export interface PreviewProps {
  pagina: PaginaLanding
  dispositivo: Dispositivo
  selecionado: string | null
  onSelecionar: (id: string) => void
}

export function Preview({ pagina, dispositivo, selecionado, onSelecionar }: PreviewProps) {
  const quadro = useRef<HTMLIFrameElement>(null)
  const rolagem = useRef(0)
  /** Marca que a seleção nasceu de um clique aqui dentro — não rolar até ela. */
  const deDentro = useRef(false)

  const alvo = useMemo(() => comMarcadores(pagina), [pagina])

  /**
   * O documento só é trocado depois que o operador para de digitar.
   *
   * Sem isso, cada tecla no inspetor reconstrói e reparseia a página inteira —
   * o quadro pisca, e a digitação fica com um atraso perceptível numa página
   * de dez blocos.
   */
  const [html, setHtml] = useState(alvo)
  useEffect(() => {
    const t = setTimeout(() => setHtml(alvo), 140)
    return () => clearTimeout(t)
  }, [alvo])

  /**
   * A seleção é aplicada no DOM, não no HTML.
   *
   * Marcá-la no `srcDoc` recarregaria o documento inteiro só para trocar uma
   * borda — o que significa esperar o debounce e perder a rolagem a cada
   * clique. Aqui é um atributo, e o realce aparece na hora.
   */
  const aplicarSelecao = useCallback(
    (rolar: boolean) => {
      const doc = quadro.current?.contentDocument
      if (!doc) return
      for (const el of Array.from(doc.querySelectorAll('[data-sel]'))) {
        el.removeAttribute('data-sel')
      }
      if (!selecionado) return
      const el = doc.querySelector(`[data-bloco="${CSS.escape(selecionado)}"]`)
      if (!el) return
      el.setAttribute('data-sel', '1')
      if (rolar) el.scrollIntoView({ block: 'center', behavior: 'smooth' })
    },
    [selecionado],
  )

  useEffect(() => {
    aplicarSelecao(!deDentro.current)
    deDentro.current = false
  }, [aplicarSelecao])

  /**
   * Attaches the click handler once the iframe has parsed its document.
   *
   * On `load`, not in an effect: `srcDoc` replaces the document on every
   * keystroke in the inspector, and an effect fires before the new one exists.
   */
  const aoCarregar = useCallback(() => {
    const doc = quadro.current?.contentDocument
    if (!doc) return

    doc.addEventListener('click', (ev) => {
      const el = (ev.target as HTMLElement | null)?.closest('[data-bloco]')
      const id = el?.getAttribute('data-bloco')
      if (!id) return
      deDentro.current = true
      onSelecionar(id)
    })

    doc.addEventListener(
      'scroll',
      () => {
        rolagem.current = doc.documentElement.scrollTop || doc.body.scrollTop
      },
      { passive: true },
    )

    // Um link clicado na prévia navegaria o iframe para fora da página que se
    // está editando — e não haveria como voltar sem recarregar o editor.
    doc.addEventListener('submit', (ev) => ev.preventDefault())
    for (const a of Array.from(doc.querySelectorAll('a'))) {
      a.addEventListener('click', (ev) => ev.preventDefault())
    }

    // O documento é novo: devolver a rolagem antes de qualquer coisa, senão
    // quem edita o rodapé volta ao topo a cada pausa na digitação.
    if (rolagem.current) doc.documentElement.scrollTop = rolagem.current
    aplicarSelecao(false)
  }, [onSelecionar, aplicarSelecao])

  const largura = LARGURAS[dispositivo]

  return (
    <div className="flex h-full justify-center overflow-auto bg-slate-100 p-6">
      <div
        className="h-fit shrink-0 overflow-hidden rounded-xl bg-white shadow-lg ring-1 ring-slate-900/10 transition-[width] duration-200"
        style={{ width: largura, maxWidth: '100%' }}
      >
        <iframe
          ref={quadro}
          title="Prévia da landing page"
          srcDoc={html}
          onLoad={aoCarregar}
          // `allow-same-origin` sem `allow-scripts`: o editor lê o documento,
          // e o JavaScript da própria página continua sem rodar — nenhum pixel
          // dispara e nenhum lead de teste é criado enquanto alguém arruma um
          // texto.
          sandbox="allow-same-origin"
          className="h-[calc(100dvh-190px)] w-full border-0"
        />
      </div>
    </div>
  )
}

/** Short label for a block, used by the outline and the inspector header. */
export function rotuloBloco(b: Bloco, nomes: Record<string, { nome: string }>): string {
  const p = b.props as Record<string, unknown>
  const titulo = typeof p.titulo === 'string' && p.titulo.trim() ? p.titulo.trim() : null
  return titulo ? `${nomes[b.tipo]?.nome ?? b.tipo}: ${titulo}` : (nomes[b.tipo]?.nome ?? b.tipo)
}
